import React, { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "react-query";
import dayjs from "dayjs";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Link,
  IconButton,
  LinearProgress,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme, lighten, darken } from "@mui/material/styles";
import {
  ArrowBack,
  AutoAwesome,
  AccessTime as AccessTimeIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  EventAvailable as EventAvailableIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Insights as InsightsIcon,
  PictureAsPdf as PictureAsPdfIcon,
  PowerSettingsNew as PowerSettingsNewIcon,
  Send as SendIcon,
  Groups as GroupsIcon,
  SupportAgent as SupportAgentIcon,
  Tab as TabIcon,
  Timer as TimerIcon,
  Timelapse as TimelapseIcon,
  TrendingDown as TrendingDownIcon,
  WarningAmber as WarningAmberIcon,
  Wifi as WifiIcon,
  Assignment as AssignmentIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { getUserOperatorAnalytics, getDashboardMetricInsights } from "../../../api/client";
import { formatDateTimePtBR } from "../../../utils/dateTime";
import MetricAiInsightsDialog from "../../../components/MetricAiInsightsDialog";
import { ensureNamespaceLoaded } from "../../../i18n/lazyNamespaces";
import { useAuthContext } from "../../../context/AuthContext";
import { useTeamSocketPresence } from "../../../hooks/useTeamSocketPresence";
import { getTeamPresenceKind } from "../../../utils/teamPresence";
import { exportOperatorTasksReportPdf } from "./operatorTasksReportPdf";
import UserTimeClockPanel from "./UserTimeClockPanel";
import { StageTimeAvgChart } from "../../TeamTasks/components/TaskAnalyticsPanels";
import { formatTaskDisplayName } from "../../TeamTasks/utils/taskDisplayName";

const BR = (x) => (Number.isFinite(x) ? x : Number(x ?? 0)).toLocaleString("pt-BR");

const buildSeriesFromTheme = (theme) => {
  const p = theme.palette;
  return [
    p.primary.main,
    p.secondary?.main ?? p.info.main,
    p.warning.main,
    p.success.main,
    p.error.main,
    p.info.main,
    darken(p.primary.main, 0.2),
    lighten(p.primary.main, 0.35),
  ];
};

/** Mesmo critério do dashboard (Atendimento): dias/horas/minutos compactos */
const secToHuman = (sec) => {
  const s = Math.max(0, Math.round(Number(sec || 0)));
  if (!s) return "0m";
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d${h ? ` ${h}h` : ""}`;
  if (h > 0) return `${h}h${m ? ` ${m}m` : ""}`;
  return `${m || 1}m`;
};

const fmtDurationSec = (sec) => {
  const n = Number(sec);
  if (!Number.isFinite(n) || n < 0) return "—";
  if (n < 60) return `${Math.round(n)}s`;
  const m = Math.floor(n / 60);
  const s = Math.round(n % 60);
  return `${m}min ${s}s`;
};

const hourLabel = (h) => `${String(h).padStart(2, "0")}:00`;

function peakHourSummary(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const max = Math.max(0, ...list.map((row) => Number(row.value) || 0));
  if (!max) return null;
  const hours = list.filter((row) => Number(row.value) === max).map((row) => hourLabel(row.hour));
  return { max, label: hours.join(" · ") };
}

function HourDistributionCard({
  title,
  hint,
  peakText,
  rows,
  maxValue,
  emptyLabel,
  colHour,
  colDistribution,
  colQty,
  onAi,
  aiBusy,
  explainAi,
  explainAiAria,
  tableSx,
}) {
  return (
    <Grid item xs={12} md={6}>
      <Card elevation={1} sx={{ height: "100%", ...dashboardSectionCardSx }}>
        <CardContent sx={{ pt: 2, pb: 2 }}>
          <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 0.5 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {title}
              </Typography>
            </Box>
            {onAi ? (
              <Tooltip title={explainAi}>
                <span>
                  <IconButton
                    size="small"
                    onClick={onAi}
                    disabled={aiBusy}
                    aria-label={explainAiAria}
                    sx={{ mt: -0.25 }}
                  >
                    <AutoAwesome fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
          </Stack>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: peakText ? 0.5 : 1.5 }}>
            {hint}
          </Typography>
          {peakText ? (
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5 }}>
              {peakText}
            </Typography>
          ) : null}
          <TableContainer variant="outlined" sx={{ bgcolor: "background.paper", borderRadius: 1, maxHeight: 360 }}>
            <Table size="small" stickyHeader sx={tableSx}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 64 }}>{colHour}</TableCell>
                  <TableCell>{colDistribution}</TableCell>
                  <TableCell align="right" sx={{ width: 72 }}>
                    {colQty}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length ? (
                  rows.map((row) => (
                    <TableRow key={row.hour} hover>
                      <TableCell sx={{ fontVariantNumeric: "tabular-nums" }}>{hourLabel(row.hour)}</TableCell>
                      <TableCell sx={{ py: 1.25 }}>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(100, (Number(row.value) / maxValue) * 100)}
                          sx={{
                            height: 10,
                            borderRadius: 1,
                            bgcolor: (th) => alpha(th.palette.primary.main, 0.08),
                            "& .MuiLinearProgress-bar": { borderRadius: 1 },
                          }}
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                        {row.value}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 3, color: "text.secondary" }}>
                      <Typography variant="body2">{emptyLabel}</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Grid>
  );
}

const formatDayBr = (day) => {
  const match = String(day || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return String(day || "");
  return `${match[3]}/${match[2]}`;
};

function ProductivityMix({ data, t }) {
  const slices = Array.isArray(data?.slices) ? data.slices : [];
  const chartRows = slices.map((slice) => ({
    name: t(`userOperatorAnalytics.prod_${slice.key}`),
    sec: Number(slice.sec || 0),
    hours: Math.round((Number(slice.sec || 0) / 3600) * 10) / 10,
    share: slice.sharePct,
    key: slice.key,
  }));
  const colors = {
    atendimento: "#1976d2",
    interno: "#7b1fa2",
    tarefas: "#ef6c00",
    compromissos: "#2e7d32",
  };
  return (
    <Box sx={{ mb: 1 }}>
      <AnalyticsSectionHeader
        icon={<InsightsIcon fontSize="small" />}
        title={t("userOperatorAnalytics.prodTitle")}
        hint={t("userOperatorAnalytics.prodHint")}
      />
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Card elevation={1} sx={dashboardSectionCardSx}>
            <CardContent>
              <Box sx={{ width: "100%", height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartRows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" interval={0} tick={{ fontSize: 12 }} />
                    <YAxis allowDecimals={false} width={36} />
                    <ChartTooltip formatter={(value) => [`${value} h`, t("userOperatorAnalytics.prodHours")]} />
                    <Bar dataKey="hours" radius={[6, 6, 0, 0]}>
                      {chartRows.map((row) => (
                        <Cell key={row.key} fill={colors[row.key] || "#1976d2"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card elevation={1} sx={{ height: "100%", ...dashboardSectionCardSx }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                {t("userOperatorAnalytics.prodTotal")}
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {secToHuman(data?.totalSec)}
              </Typography>
              <Stack spacing={0.75}>
                {slices.map((slice) => (
                  <Typography key={slice.key} variant="body2">
                    {t(`userOperatorAnalytics.prod_${slice.key}`)}: {secToHuman(slice.sec)} · {slice.sharePct}%
                    {slice.count ? ` · ${slice.count}` : ""}
                    {slice.key === "compromissos" && Number(slice.videoCount) > 0
                      ? ` · ${t("userOperatorAnalytics.prodVideoMark", { count: slice.videoCount })}`
                      : ""}
                  </Typography>
                ))}
              </Stack>
              <Box sx={{ mt: 2, pt: 1.5, borderTop: 1, borderColor: "divider" }}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t("userOperatorAnalytics.prodVideoTitle")}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: "success.main" }}>
                  {secToHuman(data?.videoSec)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t("userOperatorAnalytics.prodVideoSub", { count: data?.videoCount || 0 })}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

function DailyFlowChart({
  title,
  hint,
  data,
  receivedLabel,
  completedLabel,
  receivedColor,
  completedColor,
  onAi,
  aiBusy,
  explainAi,
  explainAiAria,
}) {
  return (
    <Grid item xs={12}>
      <Card elevation={1} sx={dashboardSectionCardSx}>
        <CardContent sx={{ pt: 2, pb: 2 }}>
          <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 0.5 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {title}
              </Typography>
            </Box>
            {onAi ? (
              <Tooltip title={explainAi}>
                <span>
                  <IconButton
                    size="small"
                    onClick={onAi}
                    disabled={aiBusy}
                    aria-label={explainAiAria}
                    sx={{ mt: -0.25 }}
                  >
                    <AutoAwesome fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
          </Stack>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
            {hint}
          </Typography>
          <Box sx={{ width: "100%", height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" tickFormatter={formatDayBr} minTickGap={28} />
                <YAxis allowDecimals={false} width={36} />
                <ChartTooltip
                  labelFormatter={(label) => {
                    const match = String(label || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
                    return match ? `${match[3]}/${match[2]}/${match[1]}` : label;
                  }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="received"
                  name={receivedLabel}
                  stroke={receivedColor}
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="completed"
                  name={completedLabel}
                  stroke={completedColor}
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );
}

/** Resumo de solicitações por status (mesmo critério do dashboard de atendimento). */
function resolveStatusSummary(cards, funil, t) {
  const by = cards?.byStatus || {};
  const funilMap = {};
  if (Array.isArray(funil)) {
    for (const row of funil) {
      const n = String(row?.name || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();
      const y = Number(row?.y ?? row?.value ?? 0) || 0;
      if (n.startsWith("pend")) funilMap.pendentes = y;
      else if (n.includes("atendimento")) funilMap.emAtendimento = y;
      else if (n.includes("futuro")) funilMap.contatoFuturo = y;
      else if (n.startsWith("conclu")) funilMap.concluidas = y;
      else if (n.startsWith("perd")) funilMap.perdidas = y;
    }
  }
  const n = (key) => {
    const v = by[key];
    if (v != null && Number.isFinite(Number(v))) return Number(v);
    if (funilMap[key] != null) return funilMap[key];
    return 0;
  };
  return [
    {
      key: "pendentes",
      label: t("userOperatorAnalytics.statusPending"),
      value: n("pendentes"),
      paletteKey: "pending",
      icon: HourglassEmptyIcon,
    },
    {
      key: "emAtendimento",
      label: t("userOperatorAnalytics.statusInProgress"),
      value: n("emAtendimento"),
      paletteKey: "inProgress",
      icon: SupportAgentIcon,
    },
    {
      key: "contatoFuturo",
      label: t("userOperatorAnalytics.statusFuture"),
      value: n("contatoFuturo"),
      paletteKey: "future",
      icon: EventAvailableIcon,
    },
    {
      key: "concluidas",
      label: t("userOperatorAnalytics.statusCompleted"),
      value: n("concluidas"),
      paletteKey: "success",
      icon: CheckCircleOutlineIcon,
    },
    {
      key: "perdidas",
      label: t("userOperatorAnalytics.statusLost"),
      value: n("perdidas"),
      paletteKey: "unsuccessful",
    icon: TrendingDownIcon,
  },
];
}

/** Situação das tarefas do responsável no período (grupos exclusivos). */
function resolveTaskStatusSummary(teamTasks, t) {
  const s = teamTasks?.summary || {};
  const n = (key) => {
    const v = Number(s[key]);
    return Number.isFinite(v) ? v : 0;
  };
  return [
    {
      key: "aFazer",
      label: t("userOperatorAnalytics.taskStatusTodo"),
      value: n("aFazer"),
      paletteKey: "pending",
      icon: HourglassEmptyIcon,
    },
    {
      key: "emAndamento",
      label: t("userOperatorAnalytics.taskStatusDoing"),
      value: n("emAndamento"),
      paletteKey: "inProgress",
      icon: TimelapseIcon,
    },
    {
      key: "paraDepois",
      label: t("userOperatorAnalytics.taskStatusLater"),
      value: n("paraDepois"),
      paletteKey: "future",
      icon: EventAvailableIcon,
    },
    {
      key: "concluidas",
      label: t("userOperatorAnalytics.taskStatusDone"),
      value: n("concluidas"),
      paletteKey: "success",
      icon: CheckCircleOutlineIcon,
    },
    {
      key: "atrasadas",
      label: t("userOperatorAnalytics.taskStatusOverdue"),
      value: n("atrasadas"),
      paletteKey: "unsuccessful",
      icon: WarningAmberIcon,
    },
  ];
}

function buildAiPayload(analytics, period, t, operatorUserId) {
  const cards = analytics?.cards || {};
  const charts = analytics?.charts || {};
  const user = analytics?.user || {};
  const taskSummary = analytics?.teamTasks?.summary || {};
  const statusSummary = resolveStatusSummary(cards, charts.funil, t);
  const taskStatusSummary = resolveTaskStatusSummary(analytics?.teamTasks, t);
  const metricValue = [
    ...statusSummary.map((s) => ({ name: s.label, value: s.value })),
    ...(Array.isArray(charts.serieTemporal) ? charts.serieTemporal : []).map((d) => ({
      day: d.day,
      value: d.value,
    })),
    ...(Array.isArray(charts.motivosTop) ? charts.motivosTop : []).map((m) => ({
      path: m.path,
      value: m.value,
    })),
    ...(Array.isArray(analytics.channels) ? analytics.channels : []).map((c) => ({
      name: `${t("userOperatorAnalytics.aiChannelPrefix")}: ${c.name}`,
      value: c.value,
    })),
    ...(Array.isArray(analytics.interactionByHour) ? analytics.interactionByHour : []).map((h) => ({
      name: `${t("userOperatorAnalytics.aiHourPrefix")} ${hourLabel(h.hour)}`,
      value: h.value,
    })),
    ...taskStatusSummary.map((s) => ({
      name: `${t("userOperatorAnalytics.tasksStatusSummary")}: ${s.label}`,
      value: s.value,
    })),
    ...(Array.isArray(analytics?.teamTasks?.receivedByHour) ? analytics.teamTasks.receivedByHour : []).map(
      (h) => ({
        name: `${t("userOperatorAnalytics.tasksReceivedHours")} ${hourLabel(h.hour)}`,
        value: h.value,
      })
    ),
    ...(Array.isArray(analytics?.teamTasks?.completedByHour) ? analytics.teamTasks.completedByHour : []).map(
      (h) => ({
        name: `${t("userOperatorAnalytics.tasksCompletedHours")} ${hourLabel(h.hour)}`,
        value: h.value,
      })
    ),
  ];

  const summaryContext = {
    escopoAtendente: true,
    idUsuarioOperador: Number(operatorUserId) > 0 ? Number(operatorUserId) : null,
    nomeAtendente: user.nome || "",
    totalSolicitacoes: cards.totalAtendimentos ?? null,
    porStatus: Object.fromEntries(statusSummary.map((s) => [s.key, s.value])),
    tmaMedioSec: cards.avgTMAsec ?? null,
    tmeMedioSec: cards.avgTMEsec ?? null,
    pessoasContatadas: analytics.operatorDistinctClients ?? null,
    mensagensEnviadasNoPeriodo: analytics.operatorMessagesTotal ?? null,
    pesquisasConcluidas: analytics.surveys?.completedSessions ?? null,
    topMotivos: (charts.motivosTop || []).slice(0, 10).map((x) => x.path),
    resumoPesquisas: (analytics.surveys?.bySurvey || []).slice(0, 8).map((s) => ({
      titulo: s.surveyTitle,
      concluidas: s.completed,
      notaMedia: s.avgScore,
    })),
    custoMedioPorAtendimento: analytics.laborCost?.labor?.custo_medio_por_atendimento ?? null,
    custoOcioso: analytics.laborCost?.labor?.custo_ocioso ?? null,
    custoHora: analytics.laborCost?.labor?.custo_hora ?? null,
    ocupacaoPct: analytics.laborCost?.labor?.ocupacao_pct ?? null,
    horasProdutivas: analytics.laborCost?.labor?.horas_produtivas ?? null,
    horasOciosas: analytics.laborCost?.labor?.horas_ociosas ?? null,
    tarefasTotal: taskSummary.total ?? null,
    tarefasPorSituacao: Object.fromEntries(taskStatusSummary.map((s) => [s.key, s.value])),
    tarefasConcluidasNoPeriodo: taskSummary.completedInPeriod ?? analytics?.teamTasks?.tasksCompleted ?? null,
    tarefasNoPrazo: taskSummary.noPrazo ?? null,
    tarefasForaDoPrazo: taskSummary.foraDoPrazo ?? null,
    tarefasNoPrazoPct: taskSummary.onTimePct ?? null,
    tarefasTempoMedioExecucaoSec: taskSummary.avgCycleSec ?? null,
    tarefasAtrasadasAgora: taskSummary.atrasadasAgora ?? null,
    tarefasObrigatoriasAbertas: taskSummary.obrigatoriasAbertas ?? null,
    tarefasEsforcoTotalSec: analytics?.teamTasks?.totalEffortSec ?? null,
  };

  const metricHuman = [
    `${t("userOperatorAnalytics.kpiTotal")}: ${BR(cards.totalAtendimentos ?? 0)}`,
    ...statusSummary.map((s) => `${s.label}: ${BR(s.value)}`),
    `TMA: ${secToHuman(cards.avgTMAsec)}`,
    `TME: ${secToHuman(cards.avgTMEsec)}`,
    `${t("userOperatorAnalytics.kpiPeopleContacted")}: ${BR(analytics.operatorDistinctClients ?? 0)}`,
    `${t("userOperatorAnalytics.kpiTasksPeriod")}: ${BR(taskSummary.total ?? 0)}`,
    ...taskStatusSummary.map((s) => `${s.label}: ${BR(s.value)}`),
    `${t("userOperatorAnalytics.kpiCycle")}: ${
      taskSummary.avgCycleSec == null ? "—" : secToHuman(taskSummary.avgCycleSec)
    }`,
    `${t("userOperatorAnalytics.kpiOnTime")}: ${
      taskSummary.onTimePct == null ? "—" : `${taskSummary.onTimePct}%`
    }`,
    `${t("userOperatorAnalytics.kpiOverdueNow")}: ${BR(taskSummary.atrasadasAgora ?? 0)}`,
  ].join(" · ");

  return {
    metric: "operator_performance",
    metricName: t("userOperatorAnalytics.aiMetricName"),
    metricLabel: t("userOperatorAnalytics.aiMetricLabel"),
    metricDefinition: t("userOperatorAnalytics.aiMetricDefinition"),
    metricValue,
    metricHuman,
    period,
    summaryContext,
    filters: {},
    fullSnapshot: {
      user: { nome: user.nome, cargo: user.cargoNome, isOnline: user.isOnline },
      period,
      cards,
      operatorDistinctClients: analytics.operatorDistinctClients,
      surveys: analytics.surveys,
      channels: analytics.channels,
      teamTasks: analytics.teamTasks
        ? {
            tasksCompleted: analytics.teamTasks.tasksCompleted,
            totalEffortSec: analytics.teamTasks.totalEffortSec,
            avgEffortSec: analytics.teamTasks.avgEffortSec,
            summary: analytics.teamTasks.summary || null,
            receivedByHour: analytics.teamTasks.receivedByHour || [],
            completedByHour: analytics.teamTasks.completedByHour || [],
            dailyFlow: analytics.teamTasks.dailyFlow || [],
          }
        : null,
    },
  };
}

function AnalyticsSectionHeader({ icon, title, hint, action, tone = "primary" }) {
  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1.5}
      alignItems={{ xs: "stretch", sm: "center" }}
      justifyContent="space-between"
      sx={{
        mb: 2,
        px: 2,
        py: 1.5,
        borderRadius: 1.5,
        border: 1,
        borderColor: "divider",
        bgcolor: (th) => alpha(th.palette[tone].main, th.palette.mode === "dark" ? 0.14 : 0.06),
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
        <Avatar sx={{ bgcolor: `${tone}.main`, color: `${tone}.contrastText`, width: 40, height: 40 }}>
          {icon}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {hint}
          </Typography>
        </Box>
      </Stack>
      {action ? <Box sx={{ flexShrink: 0 }}>{action}</Box> : null}
    </Stack>
  );
}

function mergeOperatorMetricCfg(analytics, period, t, operatorUserId, partial) {
  const base = buildAiPayload(analytics, period, t, operatorUserId);
  return {
    ...base,
    ...partial,
    summaryContext: { ...base.summaryContext, ...(partial.summaryContext || {}) },
  };
}

function openRequestPopup(leadId) {
  const id = Number(leadId);
  if (!Number.isFinite(id) || id <= 0) return;
  const width = Math.min(1280, window.screen.availWidth - 48);
  const height = Math.min(900, window.screen.availHeight - 48);
  const offset = (id % 6) * 28;
  window.open(
    `/popup-call/${id}`,
    `popup-call-${id}`,
    `width=${width},height=${height},left=${offset},top=${offset},scrollbars=yes,resizable=yes`
  );
}

function OperatorKpiCard({
  loading,
  icon,
  title,
  value,
  color,
  sub,
  onAi,
  explainAi,
  explainAiAria,
  aiBusy,
  onOpen,
}) {
  if (loading) {
    return (
      <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
        <Skeleton variant="rounded" height={118} sx={{ width: "100%", borderRadius: 1 }} />
      </Grid>
    );
  }
  return (
    <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
      <Card
        elevation={1}
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          border: 1,
          borderColor: "divider",
          boxShadow: (th) =>
            th.palette.mode === "dark"
              ? `0 1px 4px ${alpha("#000", 0.35)}`
              : `0 1px 3px ${alpha(th.palette.common.black, 0.08)}`,
          cursor: onOpen ? "pointer" : "default",
          "&:hover": onOpen ? { borderColor: color } : undefined,
        }}
        onClick={onOpen || undefined}
      >
        <CardContent sx={{ flex: 1 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar sx={{ bgcolor: color, width: 44, height: 44 }}>{icon}</Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ flex: 1, minWidth: 0 }} noWrap>
                  {title}
                </Typography>
                {onAi ? (
                  <Tooltip title={explainAi}>
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        onAi();
                      }}
                      disabled={aiBusy}
                      aria-label={explainAiAria}
                    >
                      <AutoAwesome fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                ) : null}
              </Stack>
              <Typography variant="h4" sx={{ color, lineHeight: 1.15, fontWeight: 600 }}>
                {value}
              </Typography>
              {sub ? (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                  {sub}
                </Typography>
              ) : null}
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Grid>
  );
}

const dashboardSectionCardSx = {
  border: 1,
  borderColor: "divider",
  boxShadow: (th) =>
    th.palette.mode === "dark"
      ? `0 1px 4px ${alpha("#000", 0.35)}`
      : `0 1px 3px ${alpha(th.palette.common.black, 0.08)}`,
};

/** Alinhado a TableContainer/Table de telas admin (ex.: WorkspacesTabPanel): outlined, cabeçalho fixo, zebra e hover */
function analyticsDataTableSx(theme) {
  const headBg = alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.14 : 0.07);
  return {
    "& .MuiTableCell-head": {
      fontWeight: 600,
      fontSize: "0.75rem",
      letterSpacing: "0.02em",
      color: theme.palette.text.secondary,
      backgroundColor: headBg,
      borderBottom: `1px solid ${theme.palette.divider}`,
      whiteSpace: "nowrap",
      py: 1.125,
    },
    "& .MuiTableCell-body": {
      fontSize: "0.8125rem",
      py: 1.125,
      borderColor: theme.palette.divider,
      verticalAlign: "middle",
    },
    "& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even) .MuiTableCell-body": {
      backgroundColor: alpha(theme.palette.action.hover, theme.palette.mode === "dark" ? 0.06 : 0.04),
    },
  };
}

const UserOperatorAnalytics = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const dataTableSx = useMemo(() => analyticsDataTableSx(theme), [theme]);
  const SERIES = useMemo(() => buildSeriesFromTheme(theme), [theme]);
  const { t, i18n } = useTranslation("settings");
  const { t: tChat } = useTranslation("chat");
  const [start, setStart] = useState(() => dayjs().startOf("month"));
  const [end, setEnd] = useState(() => dayjs().endOf("month"));
  const [aiOpen, setAiOpen] = useState(false);
  const [aiCfg, setAiCfg] = useState(null);
  const [aiData, setAiData] = useState(null);
  const [aiError, setAiError] = useState(null);
  const [pdfExporting, setPdfExporting] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [drill, setDrill] = useState(null);

  const { userData } = useAuthContext();
  const { tabsByUserId } = useTeamSocketPresence(userData);

  const startStr = start.format("YYYY-MM-DD");

  const openContactDrill = useCallback(
    async (kind, title) => {
      setDrill({ kind, title: title || "", loading: true, error: "", rows: [] });
      try {
        const res = await getUserOperatorAnalytics(id, {
          start: start.format("YYYY-MM-DD"),
          end: end.format("YYYY-MM-DD"),
          detail: kind,
        });
        const rows = Array.isArray(res?.tasks)
          ? res.tasks
          : Array.isArray(res?.leads)
            ? res.leads
            : kind === "people"
              ? res?.people || []
              : res?.messages || [];
        setDrill({ kind, title: title || "", loading: false, error: "", rows });
      } catch (err) {
        setDrill({
          kind,
          title: title || "",
          loading: false,
          error:
            (typeof err?.message === "string" && err.message) ||
            t("userOperatorAnalytics.drillLoadError"),
          rows: [],
        });
      }
    },
    [end, id, start, t]
  );
  const endStr = end.format("YYYY-MM-DD");

  React.useEffect(() => {
    ensureNamespaceLoaded("chat");
  }, []);

  const { data, isLoading, error, refetch, isFetching } = useQuery(
    ["userOperatorAnalytics", id, startStr, endStr],
    () => getUserOperatorAnalytics(id, { start: startStr, end: endStr }),
    { enabled: !!id, keepPreviousData: true }
  );

  const metricInsightsMutation = useMutation(
    async (cfg) => {
      const payload = {
        metric: cfg.metric,
        metricName: cfg.metricName,
        metricDefinition: cfg.metricDefinition,
        metricValue: cfg.metricValue,
        metricHuman: cfg.metricHuman,
        period: cfg.period,
        ...(cfg.metricSec != null ? { metricSec: cfg.metricSec } : {}),
        summaryContext: {
          ...cfg.summaryContext,
          analyticsEscopoAtendente: true,
          snapshot: cfg.fullSnapshot,
        },
      };
      return getDashboardMetricInsights("atendimento", payload);
    },
    {
      onSuccess: (res) => {
        setAiData(res || null);
        setAiError(null);
      },
      onError: (err) => {
        const msg =
          (typeof err?.message === "string" && err.message) ||
          (typeof err?.error === "string" && err.error) ||
          t("userOperatorAnalytics.aiError");
        setAiError(msg);
      },
    }
  );

  const openAi = useCallback(() => {
    if (!data) return;
    const period = { start: startStr, end: endStr };
    const cfg = buildAiPayload(data, period, t, id);
    setAiCfg(cfg);
    setAiOpen(true);
    setAiData(null);
    setAiError(null);
    metricInsightsMutation.mutate(cfg);
  }, [data, startStr, endStr, t, id, metricInsightsMutation]);

  const openOperatorMetricAi = useCallback(
    (partial) => {
      if (!data) return;
      const period = { start: startStr, end: endStr };
      const cfg = mergeOperatorMetricCfg(data, period, t, id, partial);
      setAiCfg(cfg);
      setAiOpen(true);
      setAiData(null);
      setAiError(null);
      metricInsightsMutation.mutate(cfg);
    },
    [data, startStr, endStr, t, id, metricInsightsMutation]
  );

  const handleExportTasksPdf = useCallback(async () => {
    if (!data?.teamTasks || pdfExporting) return;
    setPdfExporting(true);
    setPdfError(null);
    try {
      await exportOperatorTasksReportPdf({
        user: data.user,
        period: { start: startStr, end: endStr },
        teamTasks: data.teamTasks,
      });
    } catch (err) {
      setPdfError(
        (typeof err?.message === "string" && err.message) || t("userOperatorAnalytics.exportTasksPdfError")
      );
    } finally {
      setPdfExporting(false);
    }
  }, [data, startStr, endStr, pdfExporting, t]);

  const kpiLoading = isLoading && !data;

  React.useEffect(() => {
    const lng = (i18n.language || "pt-BR").toLowerCase();
    dayjs.locale(lng.startsWith("pt") ? "pt-br" : "en");
  }, [i18n.language]);

  const user = data?.user;
  const operatorUserIdNum = Number(id);
  const socketTabs =
    Number.isFinite(operatorUserIdNum) && operatorUserIdNum > 0
      ? tabsByUserId.get(operatorUserIdNum) || 0
      : 0;
  const presenceOperator = useMemo(
    () => ({
      data_logout: user?.dataLogout ?? null,
      data_ultimo_acesso: user?.ultimoAcesso ?? null,
    }),
    [user?.dataLogout, user?.ultimoAcesso]
  );
  const presenceKind = useMemo(() => {
    if (!user) return "offline";
    return getTeamPresenceKind(presenceOperator, socketTabs);
  }, [user, presenceOperator, socketTabs]);

  const presenceTooltip = useCallback(
    (kind, tabs) => {
      const tip =
        kind === "online"
          ? t("dashboardPage.live.onlinePresenceTooltip")
          : kind === "away"
            ? t("dashboardPage.live.awayPresenceTooltip")
            : t("dashboardPage.live.offlinePresenceTooltip");
      if (kind === "online" && tabs > 0) {
        return `${tip}\n${t("dashboardPage.live.openTabsCount", { count: tabs })}`;
      }
      return tip;
    },
    [t]
  );

  const presenceDotBg = useCallback(
    (kind) => {
      if (kind === "online") return theme.palette.success.main;
      if (kind === "away") return theme.palette.warning.main;
      return theme.palette.error.main;
    },
    [theme]
  );

  const presenceLabel =
    presenceKind === "online"
      ? t("dashboardPage.live.onlineNow")
      : presenceKind === "away"
        ? t("dashboardPage.live.away")
        : t("dashboardPage.live.presenceOffline");
  const PresenceStatusIcon =
    presenceKind === "online" ? WifiIcon : presenceKind === "away" ? AccessTimeIcon : PowerSettingsNewIcon;

  const cards = data?.cards || {};
  const charts = data?.charts || {};
  const laborMetrics = data?.laborCost?.labor || null;
  const teamTasks = data?.teamTasks || null;
  const showTeamTasksSection = Boolean(teamTasks?.available);
  const taskSummary = teamTasks?.summary || {};
  const showTeamTasksTable = showTeamTasksSection && (teamTasks?.tasks || []).length > 0;
  /** Só exibe KPIs de custo quando há salário/jornada configurados (senão os cards ficam com "—"). */
  const showLaborCostCards = Boolean(
    laborMetrics &&
      (laborMetrics.configured === true ||
        laborMetrics.custo_hora != null ||
        laborMetrics.custo_medio_por_atendimento != null ||
        laborMetrics.custo_ocioso != null)
  );
  const statusSummary = useMemo(
    () => resolveStatusSummary(data?.cards, data?.charts?.funil, t),
    [data?.cards, data?.charts?.funil, t]
  );
  const statusSummaryTotal = useMemo(
    () => statusSummary.reduce((acc, row) => acc + (Number(row.value) || 0), 0),
    [statusSummary]
  );
  const taskStatusSummary = useMemo(
    () => resolveTaskStatusSummary(teamTasks, t),
    [teamTasks, t]
  );
  const taskStatusTotal = useMemo(
    () => taskStatusSummary.reduce((acc, row) => acc + (Number(row.value) || 0), 0),
    [taskStatusSummary]
  );
  const receivedHours = useMemo(() => teamTasks?.receivedByHour || [], [teamTasks]);
  const completedHours = useMemo(() => teamTasks?.completedByHour || [], [teamTasks]);
  const receivedPeak = useMemo(() => peakHourSummary(receivedHours), [receivedHours]);
  const completedPeak = useMemo(() => peakHourSummary(completedHours), [completedHours]);
  const maxReceivedHour = useMemo(
    () => Math.max(1, ...receivedHours.map((row) => Number(row.value) || 0)),
    [receivedHours]
  );
  const maxCompletedHour = useMemo(
    () => Math.max(1, ...completedHours.map((row) => Number(row.value) || 0)),
    [completedHours]
  );
  const maxHourVal = useMemo(() => {
    const arr = data?.interactionByHour || [];
    return Math.max(1, ...arr.map((x) => Number(x.value) || 0));
  }, [data?.interactionByHour]);

  const errMsg =
    error &&
    (typeof error?.message === "string"
      ? error.message
      : t("userOperatorAnalytics.loadError"));

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.language?.startsWith("pt") ? "pt-br" : "en"}>
      <Box sx={{ pt: 1, pb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2, flexWrap: "wrap", gap: 1 }}>
          <Button startIcon={<ArrowBack />} onClick={() => navigate("/settings/users")} size="small">
            {t("userOperatorAnalytics.back")}
          </Button>
        </Stack>

        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box sx={{ position: "relative", flexShrink: 0 }}>
              <Avatar src={undefined} sx={{ width: 44, height: 44, bgcolor: "grey.200", color: "text.primary" }}>
                {String(user?.nome || "?")
                  .trim()
                  .split(/\s+/)
                  .slice(0, 2)
                  .map((p) => p[0]?.toUpperCase())
                  .join("") || "?"}
              </Avatar>
              {user ? (
                <Tooltip title={presenceTooltip(presenceKind, socketTabs)} placement="right" arrow>
                  <Box
                    component="span"
                    sx={{
                      position: "absolute",
                      right: 2,
                      bottom: 2,
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      bgcolor: presenceDotBg(presenceKind),
                      border: "2px solid",
                      borderColor: "background.paper",
                      cursor: "help",
                    }}
                  />
                </Tooltip>
              ) : null}
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={700}>
                {user?.nome || (isLoading ? "…" : "—")}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  columnGap: 0.5,
                  rowGap: 0.25,
                }}
              >
                {user?.email ? (
                  <Typography variant="body2" color="text.secondary" component="span">
                    {user.email}
                  </Typography>
                ) : null}
                {user?.cargoNome ? (
                  <>
                    {user?.email ? (
                      <Typography variant="body2" color="text.secondary" component="span">
                        ·
                      </Typography>
                    ) : null}
                    <Stack direction="row" alignItems="center" spacing={0.25} component="span">
                      <Typography variant="body2" color="text.secondary" component="span">
                        {user.cargoNome}
                      </Typography>
                      <Tooltip title={tChat("analytics.explainAi")}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={openAi}
                            disabled={!data || metricInsightsMutation.isLoading}
                            aria-label={tChat("analytics.explainAiAria")}
                            sx={{ p: 0.25 }}
                          >
                            <AutoAwesome sx={{ fontSize: 18 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </>
                ) : null}
              </Box>
              <Stack direction="row" spacing={0.75} sx={{ mt: 0.5 }} alignItems="center" flexWrap="wrap">
                {user ? (
                  <>
                    <Tooltip title={presenceTooltip(presenceKind, socketTabs)}>
                      <Chip
                        size="small"
                        icon={<PresenceStatusIcon sx={{ "&&": { fontSize: 16 } }} />}
                        label={presenceLabel}
                        color={presenceKind === "online" ? "success" : presenceKind === "away" ? "warning" : "error"}
                        variant="outlined"
                        sx={{ fontWeight: 600, cursor: "help" }}
                      />
                    </Tooltip>
                    {presenceKind === "online" && socketTabs > 0 ? (
                      <Chip
                        size="small"
                        icon={<TabIcon sx={{ "&&": { fontSize: 14 } }} />}
                        label={t("dashboardPage.live.openTabsCount", { count: socketTabs })}
                        variant="filled"
                        sx={{ fontWeight: 600 }}
                      />
                    ) : null}
                  </>
                ) : null}
                {user?.ultimoAcesso ? (
                  <Typography variant="caption" color="text.secondary">
                    {t("userOperatorAnalytics.lastAccess")}: {formatDateTimePtBR(user.ultimoAcesso)}
                  </Typography>
                ) : null}
              </Stack>
            </Box>
          </Stack>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ xs: "stretch", sm: "center" }}
            flexWrap={{ sm: "wrap" }}
            useFlexGap
          >
            <DatePicker
              label={t("userOperatorAnalytics.start")}
              value={start}
              onChange={(v) => v && setStart(v.startOf("day"))}
              slotProps={{ textField: { size: "small" } }}
            />
            <DatePicker
              label={t("userOperatorAnalytics.end")}
              value={end}
              onChange={(v) => v && setEnd(v.endOf("day"))}
              slotProps={{ textField: { size: "small" } }}
            />
            <Button variant="outlined" size="small" onClick={() => refetch()} disabled={isFetching}>
              {isFetching ? <CircularProgress size={16} /> : t("userOperatorAnalytics.apply")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<AutoAwesome fontSize="small" />}
              onClick={openAi}
              disabled={!data || metricInsightsMutation.isLoading}
              sx={{
                flexShrink: 0,
                alignSelf: { xs: "stretch", sm: "auto" },
                whiteSpace: "nowrap",
                px: 1.5,
              }}
            >
              {t("userOperatorAnalytics.aiButton")}
            </Button>
          </Stack>
        </Stack>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, maxWidth: 860 }}>
          {t("userOperatorAnalytics.scopeNote")}
        </Typography>

        {errMsg ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errMsg}
          </Alert>
        ) : null}

        {(isLoading && !data) || isFetching ? <LinearProgress sx={{ mb: 2 }} /> : null}

        {!isLoading && !data && !error ? (
          <Typography color="text.secondary">{t("userOperatorAnalytics.noData")}</Typography>
        ) : null}

        {data ? (
          <Stack spacing={4}>
            {data.productivity ? <ProductivityMix data={data.productivity} t={t} /> : null}
            <Box>
              <AnalyticsSectionHeader
                icon={<SupportAgentIcon fontSize="small" />}
                title={t("userOperatorAnalytics.sectionRequests")}
                hint={t("userOperatorAnalytics.sectionRequestsHint")}
              />
              <Grid container spacing={2}>
            <OperatorKpiCard
              loading={kpiLoading}
              aiBusy={metricInsightsMutation.isLoading}
              icon={<SupportAgentIcon />}
              title={t("userOperatorAnalytics.kpiTotal")}
              value={BR(cards.totalAtendimentos ?? 0)}
              color={SERIES[0]}
              sub={tChat("analytics.selectedPeriod")}
              explainAi={tChat("analytics.explainAi")}
              explainAiAria={tChat("analytics.explainAiAria")}
              onOpen={() => openContactDrill("leads:period", t("userOperatorAnalytics.kpiTotal"))}
              onAi={() =>
                openOperatorMetricAi({
                  metric: "total_solicitacoes",
                  metricName: t("userOperatorAnalytics.kpiTotal"),
                  metricLabel: t("userOperatorAnalytics.kpiTotal"),
                  metricDefinition:
                    "Quantidade de solicitações em que este usuário está como responsável no período (mesmo escopo do painel de atendimento).",
                  metricValue: cards.totalAtendimentos ?? 0,
                  metricHuman: BR(cards.totalAtendimentos ?? 0),
                })
              }
            />
            <OperatorKpiCard
              loading={kpiLoading}
              aiBusy={metricInsightsMutation.isLoading}
              icon={<AccessTimeIcon />}
              title={tChat("analytics.ahtAvg")}
              value={secToHuman(cards.avgTMAsec)}
              color={SERIES[1]}
              sub={tChat("analytics.ahtHint")}
              explainAi={tChat("analytics.explainAi")}
              explainAiAria={tChat("analytics.explainAiAria")}
              onOpen={() => openContactDrill("leads:tma", tChat("analytics.ahtAvg"))}
              onAi={() =>
                openOperatorMetricAi({
                  metric: "tma",
                  metricName: "TMA (Tempo médio de atendimento)",
                  metricLabel: tChat("analytics.ahtHint"),
                  metricDefinition:
                    "Tempo médio entre a abertura da solicitação e sua finalização, para solicitações sob responsabilidade deste atendente.",
                  metricSec: cards.avgTMAsec,
                  metricHuman: secToHuman(cards.avgTMAsec),
                  metricValue: cards.avgTMAsec,
                })
              }
            />
            <OperatorKpiCard
              loading={kpiLoading}
              aiBusy={metricInsightsMutation.isLoading}
              icon={<TimerIcon />}
              title={tChat("analytics.awtAvg")}
              value={secToHuman(cards.avgTMEsec)}
              color={SERIES[2]}
              sub={tChat("analytics.awtHint")}
              explainAi={tChat("analytics.explainAi")}
              explainAiAria={tChat("analytics.explainAiAria")}
              onOpen={() => openContactDrill("leads:tme", tChat("analytics.awtAvg"))}
              onAi={() =>
                openOperatorMetricAi({
                  metric: "tme",
                  metricName: "TME (Tempo médio de espera)",
                  metricLabel: tChat("analytics.awtHint"),
                  metricDefinition:
                    "Tempo médio de espera do cliente até o primeiro atendimento humano, nas solicitações sob responsabilidade deste atendente.",
                  metricSec: cards.avgTMEsec,
                  metricHuman: secToHuman(cards.avgTMEsec),
                  metricValue: cards.avgTMEsec,
                })
              }
            />
            {showLaborCostCards ? (
              <>
                <OperatorKpiCard
                  loading={kpiLoading}
                  aiBusy={metricInsightsMutation.isLoading}
                  icon={<AccessTimeIcon />}
                  title="Custo / atendimento"
                  value={
                    laborMetrics.custo_medio_por_atendimento != null
                      ? `R$ ${Number(laborMetrics.custo_medio_por_atendimento).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`
                      : "—"
                  }
                  color={SERIES[4] || SERIES[0]}
                  sub={
                    laborMetrics.custo_hora != null
                      ? `R$ ${Number(laborMetrics.custo_hora).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}/h`
                      : "Cadastre salário no cargo/usuário"
                  }
                  explainAi={tChat("analytics.explainAi")}
                  explainAiAria={tChat("analytics.explainAiAria")}
                  onAi={() =>
                    openOperatorMetricAi({
                      metric: "custo_medio_por_atendimento",
                      metricName: "Custo médio por atendimento",
                      metricLabel: "Custo / atendimento",
                      metricDefinition:
                        "Parte produtiva da folha ÷ volume: esforço estimado por atendimento (minutos padrão, limitado à capacidade), sem usar TMA de ciclo.",
                      metricValue: laborMetrics.custo_medio_por_atendimento,
                      metricHuman:
                        laborMetrics.custo_medio_por_atendimento != null
                          ? `R$ ${Number(laborMetrics.custo_medio_por_atendimento).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`
                          : "—",
                    })
                  }
                />
                <OperatorKpiCard
                  loading={kpiLoading}
                  aiBusy={metricInsightsMutation.isLoading}
                  icon={<GroupsIcon />}
                  title="Custo ocioso (estim.)"
                  value={
                    laborMetrics.custo_ocioso != null
                      ? `R$ ${Number(laborMetrics.custo_ocioso).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`
                      : "—"
                  }
                  color={SERIES[5] || SERIES[1]}
                  sub={data.laborCost.note || ""}
                  explainAi={tChat("analytics.explainAi")}
                  explainAiAria={tChat("analytics.explainAiAria")}
                  onAi={() =>
                    openOperatorMetricAi({
                      metric: "custo_ocioso",
                      metricName: "Custo ocioso estimado",
                      metricLabel: "Custo ocioso (estim.)",
                      metricDefinition:
                        "Parte da folha correspondente à capacidade não consumida pelo esforço estimado (minutos padrão × atendimentos). Sem timesheet; TMA de ciclo não entra.",
                      metricValue: laborMetrics.custo_ocioso,
                      metricHuman:
                        laborMetrics.custo_ocioso != null
                          ? `R$ ${Number(laborMetrics.custo_ocioso).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`
                          : "—",
                    })
                  }
                />
              </>
            ) : null}
            <OperatorKpiCard
              loading={kpiLoading}
              aiBusy={metricInsightsMutation.isLoading}
              icon={<GroupsIcon />}
              title={tChat("analytics.peopleContacted")}
              value={BR(data.operatorDistinctClients ?? 0)}
              color={SERIES[3]}
              sub={tChat("analytics.peopleContactedHint")}
              explainAi={tChat("analytics.explainAi")}
              explainAiAria={tChat("analytics.explainAiAria")}
              onOpen={() => openContactDrill("people")}
              onAi={() =>
                openOperatorMetricAi({
                  metric: "operator_clientes_distintos",
                  metricName: tChat("analytics.peopleContacted"),
                  metricLabel: tChat("analytics.peopleContacted"),
                  metricDefinition: t("userOperatorAnalytics.kpiPeopleContactedAiDefinition"),
                  metricValue: data.operatorDistinctClients ?? 0,
                  metricHuman: BR(data.operatorDistinctClients ?? 0),
                })
              }
            />
            <OperatorKpiCard
              loading={kpiLoading}
              aiBusy={metricInsightsMutation.isLoading}
              icon={<SendIcon />}
              title={t("userOperatorAnalytics.messagesSent")}
              value={BR(data.operatorMessagesTotal ?? 0)}
              color={SERIES[4]}
              sub={tChat("analytics.selectedPeriod")}
              explainAi={tChat("analytics.explainAi")}
              explainAiAria={tChat("analytics.explainAiAria")}
              onOpen={() => openContactDrill("messages")}
              onAi={() =>
                openOperatorMetricAi({
                  metric: "operator_mensagens_enviadas",
                  metricName: t("userOperatorAnalytics.messagesSent"),
                  metricLabel: t("userOperatorAnalytics.messagesSent"),
                  metricDefinition:
                    "Total de mensagens enviadas por este atendente nas conversas das solicitações sob sua responsabilidade no período.",
                  metricValue: data.operatorMessagesTotal ?? 0,
                  metricHuman: BR(data.operatorMessagesTotal ?? 0),
                })
              }
            />

            <Grid item xs={12}>
              <Card elevation={1} sx={dashboardSectionCardSx}>
                <CardContent sx={{ pt: 2, pb: 2 }}>
                  <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 0.5 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {t("userOperatorAnalytics.statusSummary")}
                      </Typography>
                    </Box>
                    <Tooltip title={tChat("analytics.explainAi")}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() =>
                            openOperatorMetricAi({
                              metric: "operator_solicitacoes_por_status",
                              metricName: t("userOperatorAnalytics.statusSummary"),
                              metricLabel: t("userOperatorAnalytics.statusSummary"),
                              metricDefinition:
                                "Quantidade de solicitações sob responsabilidade deste atendente no período, por status (pendente, em atendimento, contato futuro, concluída, perdida). Canceladas não entram no total.",
                              metricValue: statusSummary.map((s) => ({
                                status: s.key,
                                name: s.label,
                                value: s.value,
                              })),
                              metricHuman: statusSummary
                                .map((s) => `${s.label}: ${BR(s.value)}`)
                                .join(" · "),
                              summaryContext: {
                                porStatus: Object.fromEntries(
                                  statusSummary.map((s) => [s.key, s.value])
                                ),
                              },
                            })
                          }
                          disabled={metricInsightsMutation.isLoading}
                          aria-label={tChat("analytics.explainAiAria")}
                          sx={{ mt: -0.25 }}
                        >
                          <AutoAwesome fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                    {t("userOperatorAnalytics.statusSummaryHint")}
                  </Typography>
                  {kpiLoading ? (
                    <Grid container spacing={1.5}>
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Grid item xs={12} sm={6} md key={i}>
                          <Skeleton variant="rounded" height={88} sx={{ borderRadius: 1 }} />
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Grid container spacing={1.5}>
                      {statusSummary.map((row) => {
                        const Icon = row.icon;
                        const color =
                          theme.palette.status?.[row.paletteKey] ||
                          theme.palette.primary.main;
                        const pct =
                          statusSummaryTotal > 0
                            ? Math.round((Number(row.value) / statusSummaryTotal) * 100)
                            : 0;
                        return (
                          <Grid item xs={12} sm={6} md key={row.key} sx={{ display: "flex" }}>
                            <Box
                              sx={{
                                width: "100%",
                                border: 1,
                                borderColor: "divider",
                                borderRadius: 1,
                                px: 1.5,
                                py: 1.25,
                                cursor: "pointer",
                                "&:hover": { borderColor: color },
                                bgcolor: alpha(color, theme.palette.mode === "dark" ? 0.12 : 0.06),
                              }}
                              onClick={() => openContactDrill(`leads:${row.key}`, row.label)}
                            >
                              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                                <Avatar sx={{ bgcolor: color, width: 32, height: 32 }}>
                                  <Icon sx={{ fontSize: 18 }} />
                                </Avatar>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ fontWeight: 600, lineHeight: 1.2 }}
                                  noWrap
                                >
                                  {row.label}
                                </Typography>
                              </Stack>
                              <Typography
                                variant="h5"
                                sx={{ color, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}
                              >
                                {BR(row.value)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {pct}% · {tChat("analytics.selectedPeriod")}
                              </Typography>
                            </Box>
                          </Grid>
                        );
                      })}
                    </Grid>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <DailyFlowChart
              title={t("userOperatorAnalytics.leadDailyTitle")}
              hint={t("userOperatorAnalytics.leadDailyHint")}
              data={data.leadDailyFlow || []}
              receivedLabel={t("userOperatorAnalytics.dailyReceived")}
              completedLabel={t("userOperatorAnalytics.dailyCompleted")}
              receivedColor={SERIES[0]}
              completedColor={SERIES[3]}
              aiBusy={metricInsightsMutation.isLoading}
              explainAi={tChat("analytics.explainAi")}
              explainAiAria={tChat("analytics.explainAiAria")}
              onAi={() =>
                openOperatorMetricAi({
                  metric: "solicitacoes_fluxo_diario",
                  metricName: t("userOperatorAnalytics.leadDailyTitle"),
                  metricLabel: t("userOperatorAnalytics.leadDailyHint"),
                  metricDefinition:
                    "Série diária de solicitações sob responsabilidade deste usuário: recebidas no dia da abertura e concluídas no dia da última alteração da solicitação já encerrada. Serve para ver se a saída acompanha a entrada.",
                  metricValue: data.leadDailyFlow || [],
                  metricHuman: "",
                })
              }
            />

            <Grid item xs={12} md={6}>
              <Card elevation={1} sx={{ height: "100%", ...dashboardSectionCardSx }}>
                <CardContent sx={{ pt: 2, pb: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                    <InsightsIcon fontSize="small" color="action" sx={{ flexShrink: 0 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}>
                      {t("userOperatorAnalytics.typesTop")}
                    </Typography>
                    <Tooltip title={tChat("analytics.explainAi")}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() =>
                            openOperatorMetricAi({
                              metric: "motivos_top_paths",
                              metricName: t("userOperatorAnalytics.typesTop"),
                              metricLabel: t("userOperatorAnalytics.typesTop"),
                              metricDefinition:
                                "Principais combinações de tipo e subtipos nas solicitações sob responsabilidade deste atendente no período.",
                              metricValue: charts.motivosTop || [],
                              metricHuman: "",
                            })
                          }
                          disabled={metricInsightsMutation.isLoading}
                          aria-label={tChat("analytics.explainAiAria")}
                        >
                          <AutoAwesome fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                  <TableContainer
                    variant="outlined"
                    sx={{ bgcolor: "background.paper", borderRadius: 1, maxHeight: 360 }}
                  >
                    <Table size="small" stickyHeader sx={dataTableSx}>
                      <TableHead>
                        <TableRow>
                          <TableCell>{t("userOperatorAnalytics.colPath")}</TableCell>
                          <TableCell align="right" sx={{ width: 88 }}>
                            {t("userOperatorAnalytics.colQty")}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(charts.motivosTop || []).slice(0, 15).length ? (
                          (charts.motivosTop || []).slice(0, 15).map((row, idx) => (
                            <TableRow key={idx} hover>
                              <TableCell>{row.path}</TableCell>
                              <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                                {row.value}
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={2} align="center" sx={{ py: 3, color: "text.secondary" }}>
                              <Typography variant="body2">{t("userOperatorAnalytics.emptyTable")}</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card elevation={1} sx={{ height: "100%", ...dashboardSectionCardSx }}>
                <CardContent sx={{ pt: 2, pb: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}>
                      {t("userOperatorAnalytics.channels")}
                    </Typography>
                    <Tooltip title={tChat("analytics.explainAi")}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() =>
                            openOperatorMetricAi({
                              metric: "operator_canais_origem",
                              metricName: t("userOperatorAnalytics.channels"),
                              metricLabel: t("userOperatorAnalytics.channels"),
                              metricDefinition:
                                "Distribuição por canal de origem das solicitações atribuídas a este atendente no período.",
                              metricValue: data.channels || [],
                              metricHuman: "",
                            })
                          }
                          disabled={metricInsightsMutation.isLoading}
                          aria-label={tChat("analytics.explainAiAria")}
                        >
                          <AutoAwesome fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                  <TableContainer
                    variant="outlined"
                    sx={{ bgcolor: "background.paper", borderRadius: 1, maxHeight: 360 }}
                  >
                    <Table size="small" stickyHeader sx={dataTableSx}>
                      <TableHead>
                        <TableRow>
                          <TableCell>{t("userOperatorAnalytics.colChannel")}</TableCell>
                          <TableCell align="right" sx={{ width: 88 }}>
                            {t("userOperatorAnalytics.colQty")}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(data.channels || []).length ? (
                          (data.channels || []).map((row, idx) => (
                            <TableRow key={idx} hover>
                              <TableCell>{row.name}</TableCell>
                              <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                                {row.value}
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={2} align="center" sx={{ py: 3, color: "text.secondary" }}>
                              <Typography variant="body2">{t("userOperatorAnalytics.emptyTable")}</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card elevation={1} sx={dashboardSectionCardSx}>
                <CardContent sx={{ pt: 2, pb: 2 }}>
                  <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 0.5 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {t("userOperatorAnalytics.interactionHours")}
                      </Typography>
                    </Box>
                    <Tooltip title={tChat("analytics.explainAi")}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() =>
                            openOperatorMetricAi({
                              metric: "operator_interaction_hours",
                              metricName: t("userOperatorAnalytics.interactionHours"),
                              metricLabel: t("userOperatorAnalytics.interactionHours"),
                              metricDefinition:
                                "Volume de mensagens enviadas por este atendente por hora do servidor, nas solicitações sob sua responsabilidade.",
                              metricValue: (data.interactionByHour || []).map((h) => ({
                                hour: h.hour,
                                value: h.value,
                              })),
                              metricHuman: "",
                            })
                          }
                          disabled={metricInsightsMutation.isLoading}
                          aria-label={tChat("analytics.explainAiAria")}
                          sx={{ mt: -0.25 }}
                        >
                          <AutoAwesome fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                    {t("userOperatorAnalytics.interactionHoursHint")}
                  </Typography>
                  <TableContainer
                    variant="outlined"
                    sx={{ bgcolor: "background.paper", borderRadius: 1, maxHeight: 360 }}
                  >
                    <Table size="small" stickyHeader sx={dataTableSx}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: 64 }}>{t("userOperatorAnalytics.colHour")}</TableCell>
                          <TableCell>{t("userOperatorAnalytics.colDistribution")}</TableCell>
                          <TableCell align="right" sx={{ width: 72 }}>
                            {t("userOperatorAnalytics.colQty")}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(data.interactionByHour || []).length ? (
                          (data.interactionByHour || []).map((row) => (
                            <TableRow key={row.hour} hover>
                              <TableCell sx={{ fontVariantNumeric: "tabular-nums" }}>{hourLabel(row.hour)}</TableCell>
                              <TableCell sx={{ py: 1.25 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={Math.min(100, (Number(row.value) / maxHourVal) * 100)}
                                  sx={{
                                    height: 10,
                                    borderRadius: 1,
                                    bgcolor: (th) => alpha(th.palette.primary.main, 0.08),
                                    "& .MuiLinearProgress-bar": { borderRadius: 1 },
                                  }}
                                />
                              </TableCell>
                              <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                                {row.value}
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={3} align="center" sx={{ py: 3, color: "text.secondary" }}>
                              <Typography variant="body2">{t("userOperatorAnalytics.emptyHour")}</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card elevation={1} sx={dashboardSectionCardSx}>
                <CardContent sx={{ pt: 2, pb: 2 }}>
                  <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 0.5 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {t("userOperatorAnalytics.surveys")}
                      </Typography>
                    </Box>
                    <Tooltip title={tChat("analytics.explainAi")}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() =>
                            openOperatorMetricAi({
                              metric: "operator_surveys_resumo",
                              metricName: t("userOperatorAnalytics.surveys"),
                              metricLabel: t("userOperatorAnalytics.surveys"),
                              metricDefinition:
                                "Pesquisas respondidas por clientes em solicitações sob responsabilidade deste atendente: concluídas, nota média e tempo médio.",
                              metricValue: data.surveys?.bySurvey || [],
                              metricHuman: "",
                            })
                          }
                          disabled={metricInsightsMutation.isLoading}
                          aria-label={tChat("analytics.explainAiAria")}
                          sx={{ mt: -0.25 }}
                        >
                          <AutoAwesome fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                    {t("userOperatorAnalytics.surveysHint")}
                  </Typography>
                  <TableContainer
                    variant="outlined"
                    sx={{ bgcolor: "background.paper", borderRadius: 1, maxHeight: 360 }}
                  >
                    <Table size="small" stickyHeader sx={dataTableSx}>
                      <TableHead>
                        <TableRow>
                          <TableCell>{t("userOperatorAnalytics.colSurvey")}</TableCell>
                          <TableCell align="right" sx={{ width: 100 }}>
                            {t("userOperatorAnalytics.colCompleted")}
                          </TableCell>
                          <TableCell align="right" sx={{ width: 100 }}>
                            {t("userOperatorAnalytics.colAvgScore")}
                          </TableCell>
                          <TableCell align="right" sx={{ width: 112 }}>
                            {t("userOperatorAnalytics.colAvgTime")}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(data.surveys?.bySurvey || []).length ? (
                          (data.surveys?.bySurvey || []).map((row, idx) => (
                            <TableRow key={idx} hover>
                              <TableCell>{row.surveyTitle}</TableCell>
                              <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                                {row.completed}
                              </TableCell>
                              <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                                {row.avgScore != null ? row.avgScore : "—"}
                              </TableCell>
                              <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                                {row.avgTimeToCompleteSec != null ? fmtDurationSec(row.avgTimeToCompleteSec) : "—"}
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={4} align="center" sx={{ py: 3, color: "text.secondary" }}>
                              <Typography variant="body2">{t("userOperatorAnalytics.emptySurveys")}</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>

              </Grid>
            </Box>

            {showTeamTasksSection ? (
              <Box>
                <AnalyticsSectionHeader
                  tone="info"
                  icon={<AssignmentIcon fontSize="small" />}
                  title={t("userOperatorAnalytics.tasksSection")}
                  hint={t("userOperatorAnalytics.tasksSectionHint")}
                  action={
                    <Button
                      variant="outlined"
                      color="inherit"
                      size="small"
                      startIcon={
                        pdfExporting ? <CircularProgress size={14} color="inherit" /> : <PictureAsPdfIcon />
                      }
                      onClick={handleExportTasksPdf}
                      disabled={pdfExporting}
                      sx={{
                        borderColor: "divider",
                        bgcolor: "background.paper",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {pdfExporting
                        ? t("userOperatorAnalytics.exportTasksPdfBusy")
                        : t("userOperatorAnalytics.exportTasksPdf")}
                    </Button>
                  }
                />
                {pdfError ? (
                  <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPdfError(null)}>
                    {pdfError}
                  </Alert>
                ) : null}
                <Grid container spacing={2}>
                <OperatorKpiCard
                  loading={kpiLoading}
                  aiBusy={metricInsightsMutation.isLoading}
                  icon={<AssignmentIcon />}
                  title={t("userOperatorAnalytics.kpiTasksPeriod")}
                  value={BR(taskSummary.total ?? 0)}
                  color={SERIES[0]}
                  sub={t("userOperatorAnalytics.kpiTasksPeriodSub")}
                  explainAi={tChat("analytics.explainAi")}
                  explainAiAria={tChat("analytics.explainAiAria")}
                  onOpen={() => openContactDrill("tasks:period", t("userOperatorAnalytics.kpiTasksPeriod"))}
                  onAi={() =>
                    openOperatorMetricAi({
                      metric: "tarefas_periodo",
                      metricName: t("userOperatorAnalytics.kpiTasksPeriod"),
                      metricLabel: t("userOperatorAnalytics.kpiTasksPeriod"),
                      metricDefinition:
                        "Tarefas de equipe em que este usuário é responsável e que foram criadas ou concluídas no período.",
                      metricValue: taskSummary.total ?? 0,
                      metricHuman: BR(taskSummary.total ?? 0),
                    })
                  }
                />
                <OperatorKpiCard
                  loading={kpiLoading}
                  aiBusy={metricInsightsMutation.isLoading}
                  icon={<TimelapseIcon />}
                  title={t("userOperatorAnalytics.kpiCycle")}
                  value={taskSummary.avgCycleSec == null ? "—" : secToHuman(taskSummary.avgCycleSec)}
                  color={SERIES[1]}
                  sub={
                    Number(teamTasks.avgWaitingSec) > 0
                      ? t("userOperatorAnalytics.kpiCycleWaitSub", {
                          time: secToHuman(teamTasks.avgWaitingSec),
                        })
                      : t("userOperatorAnalytics.kpiCycleSub")
                  }
                  explainAi={tChat("analytics.explainAi")}
                  explainAiAria={tChat("analytics.explainAiAria")}
                  onOpen={() => openContactDrill("tasks:cycle", t("userOperatorAnalytics.kpiCycle"))}
                  onAi={() =>
                    openOperatorMetricAi({
                      metric: "tarefas_tempo_execucao",
                      metricName: t("userOperatorAnalytics.kpiCycle"),
                      metricLabel: t("userOperatorAnalytics.kpiCycleSub"),
                      metricDefinition:
                        "Tempo médio entre a criação da tarefa e a conclusão, nas tarefas concluídas por este responsável no período.",
                      metricSec: taskSummary.avgCycleSec,
                      metricHuman:
                        taskSummary.avgCycleSec == null ? "—" : secToHuman(taskSummary.avgCycleSec),
                      metricValue: taskSummary.avgCycleSec,
                    })
                  }
                />
                <OperatorKpiCard
                  loading={kpiLoading}
                  aiBusy={metricInsightsMutation.isLoading}
                  icon={<CheckCircleOutlineIcon />}
                  title={t("userOperatorAnalytics.kpiOnTime")}
                  value={taskSummary.onTimePct == null ? "—" : `${taskSummary.onTimePct}%`}
                  color={SERIES[3]}
                  sub={
                    Number(taskSummary.withDue) > 0
                      ? t("userOperatorAnalytics.kpiOnTimeSub", {
                          onTime: BR(taskSummary.noPrazo ?? 0),
                          withDue: BR(taskSummary.withDue ?? 0),
                        })
                      : t("userOperatorAnalytics.kpiOnTimeEmpty")
                  }
                  explainAi={tChat("analytics.explainAi")}
                  explainAiAria={tChat("analytics.explainAiAria")}
                  onOpen={() => openContactDrill("tasks:onTime", t("userOperatorAnalytics.kpiOnTime"))}
                  onAi={() =>
                    openOperatorMetricAi({
                      metric: "tarefas_no_prazo",
                      metricName: t("userOperatorAnalytics.kpiOnTime"),
                      metricLabel: t("userOperatorAnalytics.kpiOnTime"),
                      metricDefinition:
                        "Percentual de tarefas concluídas no período que tinham prazo e foram concluídas até o dia do vencimento. O horário dentro do dia não conta como atraso.",
                      metricValue: taskSummary.onTimePct,
                      metricHuman:
                        taskSummary.onTimePct == null
                          ? "—"
                          : `${taskSummary.onTimePct}% (${taskSummary.noPrazo ?? 0}/${taskSummary.withDue ?? 0})`,
                    })
                  }
                />
                <OperatorKpiCard
                  loading={kpiLoading}
                  aiBusy={metricInsightsMutation.isLoading}
                  icon={<WarningAmberIcon />}
                  title={t("userOperatorAnalytics.kpiOverdueNow")}
                  value={BR(taskSummary.atrasadasAgora ?? 0)}
                  color={SERIES[4] || SERIES[2]}
                  sub={t("userOperatorAnalytics.kpiOverdueNowSub")}
                  explainAi={tChat("analytics.explainAi")}
                  explainAiAria={tChat("analytics.explainAiAria")}
                  onOpen={() => openContactDrill("tasks:overdueNow", t("userOperatorAnalytics.kpiOverdueNow"))}
                  onAi={() =>
                    openOperatorMetricAi({
                      metric: "tarefas_atrasadas_agora",
                      metricName: t("userOperatorAnalytics.kpiOverdueNow"),
                      metricLabel: t("userOperatorAnalytics.kpiOverdueNow"),
                      metricDefinition:
                        "Tarefas ainda em aberto, com este usuário como responsável, cujo prazo já venceu. Independente do período selecionado.",
                      metricValue: taskSummary.atrasadasAgora ?? 0,
                      metricHuman: BR(taskSummary.atrasadasAgora ?? 0),
                    })
                  }
                />
                <OperatorKpiCard
                  loading={kpiLoading}
                  icon={<AssignmentTurnedInIcon />}
                  title={t("userOperatorAnalytics.kpiTasksCompleted")}
                  value={BR(teamTasks.tasksCompleted ?? 0)}
                  color={SERIES[0]}
                  sub={tChat("analytics.selectedPeriod")}
                  onOpen={() => openContactDrill("tasks:completed", t("userOperatorAnalytics.kpiTasksCompleted"))}
                />
                <OperatorKpiCard
                  loading={kpiLoading}
                  icon={<TimerIcon />}
                  title={t("userOperatorAnalytics.kpiTotalEffort")}
                  value={secToHuman(teamTasks.totalEffortSec)}
                  color={SERIES[2]}
                  sub={t("userOperatorAnalytics.kpiTotalEffortSub")}
                  onOpen={() => openContactDrill("tasks:effort", t("userOperatorAnalytics.kpiTotalEffort"))}
                />
                <OperatorKpiCard
                  loading={kpiLoading}
                  icon={<AccessTimeIcon />}
                  title={t("userOperatorAnalytics.kpiAvgEffort")}
                  value={secToHuman(teamTasks.avgEffortSec)}
                  color={SERIES[5] || SERIES[1]}
                  sub={t("userOperatorAnalytics.kpiAvgEffortSub")}
                  onOpen={() => openContactDrill("tasks:effort", t("userOperatorAnalytics.kpiAvgEffort"))}
                />

                <Grid item xs={12}>
                  <Card elevation={1} sx={dashboardSectionCardSx}>
                    <CardContent sx={{ pt: 2, pb: 2 }}>
                      <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 0.5 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {t("userOperatorAnalytics.tasksStatusSummary")}
                          </Typography>
                        </Box>
                        <Tooltip title={tChat("analytics.explainAi")}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() =>
                                openOperatorMetricAi({
                                  metric: "tarefas_por_situacao",
                                  metricName: t("userOperatorAnalytics.tasksStatusSummary"),
                                  metricLabel: t("userOperatorAnalytics.tasksStatusSummary"),
                                  metricDefinition:
                                    "Tarefas deste responsável criadas ou concluídas no período, em grupos exclusivos: a fazer, em andamento, para depois, concluídas e atrasadas. Atrasadas são as ainda abertas com prazo vencido até o fim do período (ou agora, se o período inclui hoje).",
                                  metricValue: taskStatusSummary.map((s) => ({
                                    status: s.key,
                                    name: s.label,
                                    value: s.value,
                                  })),
                                  metricHuman: taskStatusSummary
                                    .map((s) => `${s.label}: ${BR(s.value)}`)
                                    .join(" · "),
                                  summaryContext: {
                                    tarefasPorSituacao: Object.fromEntries(
                                      taskStatusSummary.map((s) => [s.key, s.value])
                                    ),
                                    obrigatoriasAbertas: taskSummary.obrigatoriasAbertas ?? 0,
                                  },
                                })
                              }
                              disabled={metricInsightsMutation.isLoading}
                              aria-label={tChat("analytics.explainAiAria")}
                              sx={{ mt: -0.25 }}
                            >
                              <AutoAwesome fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                        {t("userOperatorAnalytics.tasksStatusSummaryHint")}
                        {Number(taskSummary.obrigatoriasAbertas) > 0
                          ? ` ${t("userOperatorAnalytics.requiredOpenNote", {
                              count: BR(taskSummary.obrigatoriasAbertas),
                            })}`
                          : ""}
                      </Typography>
                      {kpiLoading ? (
                        <Grid container spacing={1.5}>
                          {Array.from({ length: 5 }).map((_, i) => (
                            <Grid item xs={12} sm={6} md key={i}>
                              <Skeleton variant="rounded" height={88} sx={{ borderRadius: 1 }} />
                            </Grid>
                          ))}
                        </Grid>
                      ) : (
                        <Grid container spacing={1.5}>
                          {taskStatusSummary.map((row) => {
                            const Icon = row.icon;
                            const color =
                              theme.palette.status?.[row.paletteKey] || theme.palette.primary.main;
                            const pct =
                              taskStatusTotal > 0
                                ? Math.round((Number(row.value) / taskStatusTotal) * 100)
                                : 0;
                            return (
                              <Grid item xs={12} sm={6} md key={row.key} sx={{ display: "flex" }}>
                                <Box
                                  sx={{
                                    width: "100%",
                                    border: 1,
                                    borderColor: "divider",
                                    borderRadius: 1,
                                    px: 1.5,
                                    py: 1.25,
                                    cursor: "pointer",
                                    "&:hover": { borderColor: color },
                                    bgcolor: alpha(color, theme.palette.mode === "dark" ? 0.12 : 0.06),
                                  }}
                                  onClick={() => openContactDrill(`tasks:${row.key}`, row.label)}
                                >
                                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                                    <Avatar sx={{ bgcolor: color, width: 32, height: 32 }}>
                                      <Icon sx={{ fontSize: 18 }} />
                                    </Avatar>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ fontWeight: 600, lineHeight: 1.2 }}
                                      noWrap
                                    >
                                      {row.label}
                                    </Typography>
                                  </Stack>
                                  <Typography
                                    variant="h5"
                                    sx={{ color, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}
                                  >
                                    {BR(row.value)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {pct}% · {tChat("analytics.selectedPeriod")}
                                  </Typography>
                                </Box>
                              </Grid>
                            );
                          })}
                        </Grid>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                <DailyFlowChart
                  title={t("userOperatorAnalytics.taskDailyTitle")}
                  hint={t("userOperatorAnalytics.taskDailyHint")}
                  data={teamTasks.dailyFlow || []}
                  receivedLabel={t("userOperatorAnalytics.dailyReceived")}
                  completedLabel={t("userOperatorAnalytics.dailyCompleted")}
                  receivedColor={SERIES[0]}
                  completedColor={SERIES[3]}
                  aiBusy={metricInsightsMutation.isLoading}
                  explainAi={tChat("analytics.explainAi")}
                  explainAiAria={tChat("analytics.explainAiAria")}
                  onAi={() =>
                    openOperatorMetricAi({
                      metric: "tarefas_fluxo_diario",
                      metricName: t("userOperatorAnalytics.taskDailyTitle"),
                      metricLabel: t("userOperatorAnalytics.taskDailyHint"),
                      metricDefinition:
                        "Série diária de tarefas deste responsável: recebidas no dia da atribuição e concluídas no dia da conclusão. Serve para ver se a saída acompanha a entrada.",
                      metricValue: teamTasks.dailyFlow || [],
                      metricHuman: "",
                    })
                  }
                />

                <StageTimeAvgChart
                  loading={kpiLoading}
                  rows={teamTasks.avgTimeByStage || []}
                  title={t("userOperatorAnalytics.stageTimeAvgTitle")}
                  hint={t("userOperatorAnalytics.stageTimeAvgHint")}
                  emptyLabel={t("userOperatorAnalytics.stageTimeAvgEmpty")}
                  axisLabel={t("userOperatorAnalytics.stageTimeAvgAxis")}
                  color={SERIES[5] || SERIES[1]}
                  cardSx={dashboardSectionCardSx}
                />

                <HourDistributionCard
                  title={t("userOperatorAnalytics.tasksReceivedHours")}
                  hint={t("userOperatorAnalytics.tasksReceivedHoursHint")}
                  peakText={
                    receivedPeak
                      ? t("userOperatorAnalytics.taskHourPeak", {
                          hours: receivedPeak.label,
                          count: BR(receivedPeak.max),
                        })
                      : null
                  }
                  rows={receivedHours}
                  maxValue={maxReceivedHour}
                  emptyLabel={t("userOperatorAnalytics.emptyTaskHour")}
                  colHour={t("userOperatorAnalytics.colHour")}
                  colDistribution={t("userOperatorAnalytics.colDistribution")}
                  colQty={t("userOperatorAnalytics.colQty")}
                  tableSx={dataTableSx}
                  aiBusy={metricInsightsMutation.isLoading}
                  explainAi={tChat("analytics.explainAi")}
                  explainAiAria={tChat("analytics.explainAiAria")}
                  onAi={() =>
                    openOperatorMetricAi({
                      metric: "tarefas_horarios_recebidas",
                      metricName: t("userOperatorAnalytics.tasksReceivedHours"),
                      metricLabel: t("userOperatorAnalytics.tasksReceivedHoursHint"),
                      metricDefinition:
                        "Quantidade de tarefas atribuídas a este usuário por hora do dia, no período, com base no momento da atribuição.",
                      metricValue: receivedHours.map((h) => ({ hour: h.hour, value: h.value })),
                      metricHuman: receivedPeak
                        ? `${receivedPeak.label} (${receivedPeak.max})`
                        : "—",
                    })
                  }
                />
                <HourDistributionCard
                  title={t("userOperatorAnalytics.tasksCompletedHours")}
                  hint={t("userOperatorAnalytics.tasksCompletedHoursHint")}
                  peakText={
                    completedPeak
                      ? t("userOperatorAnalytics.taskHourPeak", {
                          hours: completedPeak.label,
                          count: BR(completedPeak.max),
                        })
                      : null
                  }
                  rows={completedHours}
                  maxValue={maxCompletedHour}
                  emptyLabel={t("userOperatorAnalytics.emptyTaskHour")}
                  colHour={t("userOperatorAnalytics.colHour")}
                  colDistribution={t("userOperatorAnalytics.colDistribution")}
                  colQty={t("userOperatorAnalytics.colQty")}
                  tableSx={dataTableSx}
                  aiBusy={metricInsightsMutation.isLoading}
                  explainAi={tChat("analytics.explainAi")}
                  explainAiAria={tChat("analytics.explainAiAria")}
                  onAi={() =>
                    openOperatorMetricAi({
                      metric: "tarefas_horarios_concluidas",
                      metricName: t("userOperatorAnalytics.tasksCompletedHours"),
                      metricLabel: t("userOperatorAnalytics.tasksCompletedHoursHint"),
                      metricDefinition:
                        "Quantidade de tarefas concluídas por este responsável por hora do dia, no período, com base na data de conclusão.",
                      metricValue: completedHours.map((h) => ({ hour: h.hour, value: h.value })),
                      metricHuman: completedPeak
                        ? `${completedPeak.label} (${completedPeak.max})`
                        : "—",
                    })
                  }
                />
              
            {showTeamTasksTable ? (
              <>
                <Grid item xs={12}>
                  <Card elevation={1} sx={dashboardSectionCardSx}>
                    <CardContent sx={{ pt: 2, pb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {t("userOperatorAnalytics.tasksCompletedList")}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                        {t("userOperatorAnalytics.tasksWithDurationNote", {
                          with: Number(teamTasks.tasksWithDuration || 0),
                          total: Number(teamTasks.tasksCompleted || 0),
                        })}
                      </Typography>
                      <TableContainer
                        variant="outlined"
                        sx={{ bgcolor: "background.paper", borderRadius: 1, maxHeight: 420 }}
                      >
                        <Table size="small" stickyHeader sx={dataTableSx}>
                          <TableHead>
                            <TableRow>
                              <TableCell>{t("userOperatorAnalytics.colTask")}</TableCell>
                              <TableCell>{t("userOperatorAnalytics.colBoard")}</TableCell>
                              <TableCell sx={{ whiteSpace: "nowrap" }}>
                                {t("userOperatorAnalytics.colCompletedAt")}
                              </TableCell>
                              <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                {t("userOperatorAnalytics.colCycle")}
                              </TableCell>
                              <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                {t("userOperatorAnalytics.colWaiting")}
                              </TableCell>
                              <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                {t("userOperatorAnalytics.colReopens")}
                              </TableCell>
                              <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                {t("userOperatorAnalytics.colEffort")}
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(teamTasks.tasks || []).length ? (
                              (teamTasks.tasks || []).map((task) => (
                                <TableRow key={task.id} hover>
                                  <TableCell>
                                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {formatTaskDisplayName(task.id, task.title)}
                                      </Typography>
                                      {task.isRequired ? (
                                        <Chip
                                          size="small"
                                          label={t("userOperatorAnalytics.taskRequired")}
                                          color="warning"
                                          variant="outlined"
                                          sx={{ height: 22, fontWeight: 600 }}
                                        />
                                      ) : null}
                                    </Stack>
                                  </TableCell>
                                  <TableCell>{task.boardName || "—"}</TableCell>
                                  <TableCell sx={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                                    {task.completedAt ? formatDateTimePtBR(task.completedAt) : "—"}
                                  </TableCell>
                                  <TableCell
                                    align="right"
                                    sx={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}
                                  >
                                    {task.cycleSec != null ? secToHuman(task.cycleSec) : "—"}
                                  </TableCell>
                                  <TableCell
                                    align="right"
                                    sx={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}
                                  >
                                    {Number(task.waitingSec) > 0 ? secToHuman(task.waitingSec) : "—"}
                                  </TableCell>
                                  <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                                    {Number(task.reopenCount) > 0 ? BR(task.reopenCount) : "—"}
                                  </TableCell>
                                  <TableCell
                                    align="right"
                                    sx={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}
                                  >
                                    {task.estimatedDuration ||
                                      (task.effortSec != null ? secToHuman(task.effortSec) : "—")}
                                  </TableCell>
                                </TableRow>
                              ))
                            ) : (
                              <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ py: 3, color: "text.secondary" }}>
                                  <Typography variant="body2">{t("userOperatorAnalytics.emptyTasks")}</Typography>
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>
                </Grid>
              </>
            ) : null}
                </Grid>
              </Box>
            ) : null}
          </Stack>
        ) : null}

        <Box sx={{ mt: 3 }}>
          <UserTimeClockPanel
            userId={Number(id)}
            userName={data?.user?.nome || data?.user?.name || data?.nome}
          />
        </Box>

        <Dialog
          open={Boolean(drill)}
          onClose={() => setDrill(null)}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle sx={{ pr: 6 }}>
            {drill?.title
              ? drill.title
              : drill?.kind === "messages"
                ? t("userOperatorAnalytics.messagesDialogTitle")
                : t("userOperatorAnalytics.peopleDialogTitle")}
            <IconButton
              aria-label={t("userOperatorAnalytics.close")}
              onClick={() => setDrill(null)}
              sx={{ position: "absolute", right: 12, top: 12 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {String(drill?.kind || "").startsWith("tasks:")
                ? t("userOperatorAnalytics.taskDrillHint")
                : String(drill?.kind || "").startsWith("leads:")
                  ? t("userOperatorAnalytics.leadDrillHint")
                  : drill?.kind === "messages"
                  ? t("userOperatorAnalytics.messagesDialogHint")
                  : t("userOperatorAnalytics.peopleDialogHint")}
            </Typography>
            {drill?.loading ? <LinearProgress sx={{ mb: 2 }} /> : null}
            {drill?.error ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {drill.error}
              </Alert>
            ) : null}
            {!drill?.loading && !drill?.error && String(drill?.kind || "").startsWith("leads:") ? (
              <TableContainer sx={{ maxHeight: 480 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("userOperatorAnalytics.colProtocol")}</TableCell>
                      <TableCell>{t("userOperatorAnalytics.colPerson")}</TableCell>
                      <TableCell>{t("userOperatorAnalytics.colStatus")}</TableCell>
                      <TableCell>{t("userOperatorAnalytics.colOpenedAt")}</TableCell>
                      {drill.kind === "leads:tma" ? <TableCell>TMA</TableCell> : null}
                      {drill.kind === "leads:tme" ? <TableCell>TME</TableCell> : null}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(drill.rows || []).length ? (
                      drill.rows.map((row) => (
                        <TableRow key={row.id} hover>
                          <TableCell>
                            <Link
                              component="button"
                              type="button"
                              underline="hover"
                              onClick={() => openRequestPopup(row.id)}
                              sx={{ fontWeight: 700 }}
                            >
                              #{row.id}
                            </Link>
                          </TableCell>
                          <TableCell>{row.clientName || "—"}</TableCell>
                          <TableCell>
                            {t(`userOperatorAnalytics.status_${row.status}`, { defaultValue: row.status })}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {row.createdAt ? formatDateTimePtBR(row.createdAt) : "—"}
                          </TableCell>
                          {drill.kind === "leads:tma" ? (
                            <TableCell>{row.tmaSec ? secToHuman(row.tmaSec) : "—"}</TableCell>
                          ) : null}
                          {drill.kind === "leads:tme" ? (
                            <TableCell>{row.tmeSec ? secToHuman(row.tmeSec) : "—"}</TableCell>
                          ) : null}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={drill.kind === "leads:tma" || drill.kind === "leads:tme" ? 5 : 4} align="center" sx={{ py: 3 }}>
                          {t("userOperatorAnalytics.drillEmpty")}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : null}
            {!drill?.loading && !drill?.error && String(drill?.kind || "").startsWith("tasks:") ? (
              <TableContainer sx={{ maxHeight: 480 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("userOperatorAnalytics.colTask")}</TableCell>
                      <TableCell>{t("userOperatorAnalytics.colBoard")}</TableCell>
                      <TableCell>{t("userOperatorAnalytics.colStatus")}</TableCell>
                      <TableCell>{t("userOperatorAnalytics.colDue")}</TableCell>
                      <TableCell>{t("userOperatorAnalytics.colCompletedAt")}</TableCell>
                      {drill.kind === "tasks:onTime" ? (
                        <TableCell>{t("userOperatorAnalytics.kpiOnTime")}</TableCell>
                      ) : null}
                      <TableCell>{t("userOperatorAnalytics.colProtocol")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(drill.rows || []).length ? (
                      drill.rows.map((row) => (
                        <TableRow key={row.id} hover>
                          <TableCell>{row.title}</TableCell>
                          <TableCell>{row.boardName || "—"}</TableCell>
                          <TableCell>{row.columnName || "—"}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {row.dueAt ? formatDateTimePtBR(row.dueAt) : "—"}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {row.completedAt ? formatDateTimePtBR(row.completedAt) : "—"}
                          </TableCell>
                          {drill.kind === "tasks:onTime" ? (
                            <TableCell>
                              {row.onTime
                                ? t("userOperatorAnalytics.onTimeYes")
                                : t("userOperatorAnalytics.onTimeNo")}
                            </TableCell>
                          ) : null}
                          <TableCell>
                            {row.leadId ? (
                              <Link
                                component="button"
                                type="button"
                                underline="hover"
                                onClick={() => openRequestPopup(row.leadId)}
                                sx={{ fontWeight: 700 }}
                              >
                                #{row.leadId}
                              </Link>
                            ) : (
                              "—"
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={drill.kind === "tasks:onTime" ? 7 : 6} align="center" sx={{ py: 3 }}>
                          {t("userOperatorAnalytics.drillEmpty")}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : null}
            {!drill?.loading && !drill?.error && !String(drill?.kind || "").startsWith("tasks:") && !String(drill?.kind || "").startsWith("leads:") ? (
              <TableContainer sx={{ maxHeight: 480 }}>
                <Table size="small" stickyHeader>
                  {drill?.kind === "messages" ? (
                    <>
                      <TableHead>
                        <TableRow>
                          <TableCell>{t("userOperatorAnalytics.colWhen")}</TableCell>
                          <TableCell>{t("userOperatorAnalytics.colPerson")}</TableCell>
                          <TableCell>{t("userOperatorAnalytics.colMessage")}</TableCell>
                          <TableCell>{t("userOperatorAnalytics.colProtocol")}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(drill?.rows || []).length ? (
                          drill.rows.map((row) => (
                            <TableRow key={row.id} hover>
                              <TableCell sx={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                                {row.sentAt ? formatDateTimePtBR(row.sentAt) : "—"}
                              </TableCell>
                              <TableCell>{row.clientName || "—"}</TableCell>
                              <TableCell sx={{ whiteSpace: "pre-wrap" }}>{row.text || "—"}</TableCell>
                              <TableCell sx={{ whiteSpace: "nowrap" }}>
                                <Link
                                  component="button"
                                  type="button"
                                  underline="hover"
                                  onClick={() => openRequestPopup(row.leadId)}
                                  sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}
                                >
                                  #{row.leadId}
                                </Link>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={4} align="center" sx={{ py: 3, color: "text.secondary" }}>
                              {t("userOperatorAnalytics.drillEmpty")}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </>
                  ) : (
                    <>
                      <TableHead>
                        <TableRow>
                          <TableCell>{t("userOperatorAnalytics.colPerson")}</TableCell>
                          <TableCell align="right">{t("userOperatorAnalytics.colMessages")}</TableCell>
                          <TableCell>{t("userOperatorAnalytics.colProtocol")}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(drill?.rows || []).length ? (
                          drill.rows.map((row) => (
                            <TableRow key={row.clientId} hover>
                              <TableCell>{row.name}</TableCell>
                              <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                                {BR(row.messages)}
                              </TableCell>
                              <TableCell>
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                  {(row.leadIds || []).map((leadId) => (
                                    <Link
                                      key={leadId}
                                      component="button"
                                      type="button"
                                      underline="hover"
                                      onClick={() => openRequestPopup(leadId)}
                                      sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}
                                    >
                                      #{leadId}
                                    </Link>
                                  ))}
                                </Stack>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={3} align="center" sx={{ py: 3, color: "text.secondary" }}>
                              {t("userOperatorAnalytics.drillEmpty")}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </>
                  )}
                </Table>
              </TableContainer>
            ) : null}
          </DialogContent>
        </Dialog>

        <MetricAiInsightsDialog
          open={aiOpen}
          metric={aiCfg?.metric}
          metricName={aiCfg?.metricName}
          metricLabel={aiCfg?.metricLabel}
          metricHuman={aiCfg?.metricHuman}
          period={aiCfg?.period}
          loading={metricInsightsMutation.isLoading}
          error={aiError}
          data={aiData}
          scope="atendimento"
          basePayload={aiCfg || {}}
          onClose={() => setAiOpen(false)}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default UserOperatorAnalytics;
